import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Input,
  DatePicker,
  Select,
  Typography,
  Space,
  Tag,
  Drawer,
  Divider,
  Button,
  Form,
  List,
  Avatar,
  Image,
  Alert,
  Row,
  Col,
  Upload,
  message,
  Statistic,
  Card,
  Tooltip,
} from "antd";

import TextArea from "antd/es/input/TextArea";

import dayjs from "dayjs";
import axios from "axios";
import constants from "../constants";
import { EditOutlined, UploadOutlined } from "@ant-design/icons";

import { useAuth } from "../contexts/authContext";
import Meta from "antd/es/card/Meta";

function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

const tailLayout = {
  wrapperCol: { offset: 0, span: 24 },
};

const LeadVerification = () => {
  const { Option } = Select;
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [drawerData, setDrawerData] = useState(false);

  const [form] = Form.useForm();

  const { getToken } = useAuth();

  const fetchLeadsFromServer = async (query) => {
    try {
      const token = await getToken();
      const headers = {
        authorization: `bearer ${token}`,
      };

      const url = `${constants.NEW_DOMAIN}/api/v1/channelPartnerLeads/getLeadsForVerification`;
      const res = await axios.get(url, {
        headers,
      });

      return {
        data: res.data?.data,
      };
    } catch (error) {
      throw new Error(error?.message || "something went wrong , try again later");
    }
  };

  const updateRowAfterUpload = (id, key, records) => {
    rows.map((data) => {
      if (data._id === id) {
        setDrawerData({ ...data, [key]: records });
      }
    });

    setRows((prev) => {
      return prev.map((data) => {
        if (data._id === id) {
          return { ...data, [key]: records };
        } else {
          return data;
        }
      });
    });
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    fetchLeadsFromServer()
      .then((res) => {
        if (cancelled) return;
        setRows(res.data);
        setTotal(res.total);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || "Failed to load leads");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const updateDetails = async (form) => {
    console.log(form);

    if (!form.recordId) {
      return setError("Document Id is missing");
    }

    const urlPayment = `${constants.NEW_DOMAIN}/api/v1/channelPartnerLeads/processPaymentRequest/${drawerData._id}`;
    const urlDocument = `${constants.NEW_DOMAIN}/api/v1/channelPartnerLeads/processDocumentRequest/${drawerData._id}`;

    setLoading(true);

    try {
      const token = await getToken();
      const headers = {
        authorization: `bearer ${token}`,
      };

      const res = await axios.post(
        form.queryType === "payment" ? urlPayment : urlDocument,
        {
          amount: form.amount,
          status: form.status,
          comments: form.comments,
          recordId: form.recordId,
          name: form.documentName,
        },
        { headers }
      );
      setRows((prev) => {
        const updatedRows = prev.filter((data) => {
          if (data.recordId === drawerData.recordId) {
            return false;
          }
          return true;
        });
        return updatedRows;
      });
      setLoading(false);
      setDrawerData(false);
      message.success("Data updated successfully");
    } catch (error) {
      setLoading(false);
      setError(error.message || "something went wrong , try again later");
    }
  };

  const columns = [
    {
      title: (
        <Space direction="vertical" size={4}>
          <span>Name</span>
        </Space>
      ),
      dataIndex: "name",
      key: "name",
      render: (text) => <Typography.Text>{text}</Typography.Text>,
    },
    {
      title: (
        <Space direction="vertical" size={4}>
          <span>Phone</span>
        </Space>
      ),
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: <span>Request Type</span>,
      dataIndex: "queryType",
      key: "queryType",
      render: (data, record) => {
        return (
          <div>
            <Tag color="gold" style={{ marginBottom: "4px" }}>
              {data}
            </Tag>
            <br />
            {record.queryType === "payment" && <Tag color="blue">INR {record.amount}</Tag>}
            {record.queryType === "document" && <Tag color="blue">{record.documentName}</Tag>}
          </div>
        );
      },
    },

    {
      title: "Action",
      key: "operation",
      fixed: "right",
      width: 100,
      render: (_, record) => (
        <a
          onClick={() => {
            // form.setFieldsValue({ ...record, followupDate: record.followupDate ? dayjs(record.followupDate) : null, id: record._id });
            setDrawerData(record);
          }}
        >
          action <EditOutlined />
        </a>
      ),
    },
  ];

  return (
    <div style={{ padding: "12px" }}>
      <Typography.Title level={4} style={{ margin: "8px 0 16px" }}>
        Document Verification
      </Typography.Title>

      {error ? <Alert style={{ margin: "7px 0" }} message="Error" description={error} type="error" showIcon closable /> : null}

      <Drawer
        title="Student Details"
        closable={{ "aria-label": "Close Button" }}
        size="large"
        onClose={() => {
          setDrawerData(false);
          form.resetFields();
        }}
        destroyOnClose
        afterOpenChange={(visible) => {
          if (visible) {
            form.setFieldsValue({ ...drawerData, followupDate: drawerData.followupDate ? dayjs(drawerData.followupDate) : null, id: drawerData._id });
          } else {
            form.resetFields();
          }
        }}
        open={drawerData}
      >
        {drawerData.queryType === "payment" ? (
          <div>
            <Divider orientation="middle">
              <h2 style={{ color: "#971eff" }}>Approve Payment Details</h2>
            </Divider>
            <p>Payment Screenshot</p>
            <Image
              src={drawerData.url}
              height={200}
              style={{ objectFit: "cover", border: "1px solid #ccc" }}
              draggable={false}
              alt="screenshot"
              preview={true}
            />
            <Typography.Link href={drawerData.url} target="_blank" ellipsis>
              {drawerData.url}
            </Typography.Link>
            <Form style={{ maxWidth: 400, marginTop: "15px" }} form={form} layout="vertical" size="small" onFinish={updateDetails}>
              <Form.Item name="recordId" hidden rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="queryType" hidden rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="amount" label="Amount" rules={[{ required: true }]}>
                <Input prefix="INR" type="number" />
              </Form.Item>

              <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                <Select
                  // value={filters.paymentStatus}
                  // onChange={(v) => handleInputChange("paymentStatus")({ target: { value: v } })}
                  options={[
                    { label: "Verified", value: "verified" },
                    { label: "Need Clarification", value: "need_clarification" },
                  ]}
                  size="small"
                  style={{ width: 150 }}
                  allowClear
                />
              </Form.Item>

              <Form.Item name="comments" label="comments" rules={[{ required: true }]}>
                <TextArea rows={3} />
              </Form.Item>

              {error && <Alert style={{ margin: "7px 0" }} message="Error" description={error} type="error" showIcon closable />}

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    Update Details
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        ) : (
          <div>
            <Divider orientation="middle">
              <h2 style={{ color: "#971eff" }}>Update {drawerData.documentName}</h2>
            </Divider>
            <Image
              src={drawerData.url}
              height={200}
              style={{ objectFit: "cover", border: "1px solid #ccc" }}
              draggable={false}
              alt="screenshot"
              preview={true}
            />
            <Typography.Link href={drawerData.url} target="_blank" ellipsis>
              {drawerData.url}
            </Typography.Link>
            <Form form={form} layout="vertical" size="small" style={{ maxWidth: 400 }} onFinish={updateDetails}>
              <Form.Item name="recordId" hidden rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="queryType" hidden rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="documentName" label="Document Title" rules={[{ required: true }]}>
                <Select placeholder="Select which doc you're uploading">
                  <Option value="passport-student">Student's Passport</Option>
                  <Option value="passport-parent">Parent's Passport</Option>
                  <Option value="birth-certificate">Birth Certificate</Option>
                  <Option value="noc">NOC</Option>
                  <Option value="pancard-parent">Parent's PAN CARD</Option>
                  <Option value="passport-cover">Passport Cover</Option>
                </Select>
              </Form.Item>

              <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                <Select
                  // value={filters.paymentStatus}
                  // onChange={(v) => handleInputChange("paymentStatus")({ target: { value: v } })}
                  options={[
                    { label: "Verified", value: "verified" },
                    { label: "Need Clarification", value: "need_clarification" },
                  ]}
                  size="small"
                  style={{ width: 150 }}
                  allowClear
                />
              </Form.Item>

              <Form.Item name="comments" label="comments" rules={[{ required: true }]}>
                <TextArea rows={3} />
              </Form.Item>

              {error && <Alert style={{ margin: "7px 0" }} message="Error" description={error} type="error" showIcon closable />}

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    Update Document
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Drawer>
      <Table tableLayout="fixed" rowKey="recordId" size="small" loading={loading} dataSource={rows} columns={columns} scroll={{ x: true }} />
    </div>
  );
};

export default LeadVerification;
